import { Flashbar } from 'AWS-UI-Components-React';
import React from 'react';
import { ERROR_MESSAGE } from '../../../constants/views';
import { extractErrorMessage } from '../../../utilities';
import logger from '../../../utils/logging';
import { getJSClient } from '../../js_client';
import { ChannelMappingTable } from './channel_mapping_table';
import {
  getHCPChannelTypeOptions,
  getHCPChannelOptions,
  getCognosChannelOptions,
  getGroupCodeOptions,
  getMarketplaceIdOptions, getCognosParentChannelOptions,
} from './channel_mapping_utils';

export class ChannelMapperContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      channelMappings: [],
      dropdownOptions: {},
      errorScreenMessage: null,
      successScreenMessage: null,
      channelMappingsById: {},
      hcpChannelTypeOptions: [],
      hcpChannelOptions: [],
      originalChannelMappings: {},
    };
  }

  componentDidMount = () => {
    this._isMounted = true;
    this.client = getJSClient();
    this.initialClientTasks();
  };

  componentWillUnmount = () => {
    this._isMounted = false;
  };

  safeSetState = (...args) => {
    if (this._isMounted) this.setState(...args);
  };

  initialClientTasks = () => {
    this.refreshChannelMapping();
  };

  stageChannelMappingChanges = (modifiedChannelMappingsById) => {
    const { channelMappingsById } = this.state;
    const updatedById = { ...channelMappingsById };
    let stagedChannelMappings = [];
    if (Object.keys(modifiedChannelMappingsById).length > 0) {
      for (const id of Object.keys(modifiedChannelMappingsById)) {
        const channelMapping = { ...updatedById[id], ...modifiedChannelMappingsById[id] };
        updatedById[id] = channelMapping;
        stagedChannelMappings.push(channelMapping);
      }
    }

    this.setState({ channelMappingsById: updatedById, channelMappings: Object.values(updatedById) });

    const payload = this.convertToStageRequestPayload(stagedChannelMappings);
    this._lastStagePayload = payload;
    this.client.stageChannelMapping(this.onStageChannelMappingSuccess, this.onStageChannelMappingFailure, payload);
  };

  convertToStageRequestPayload = (stagedChannelMappings) => {
    const payload = {};
    payload.channelMappings = stagedChannelMappings;
    payload.username = this.props.user;

    return payload;
  };

  onStageChannelMappingSuccess = (data) => {
    logger.info('Fetched staged channel mappings.');
    let stagedChannelMappings = data.channelMappings;

    // Retry once if backend returned empty but we sent a non-empty payload
    if (!stagedChannelMappings.length && this._lastStagePayload &&
        this._lastStagePayload.channelMappings.length > 0 && !this._stageRetried) {
      logger.info('Empty stage response — retrying once.');
      this._stageRetried = true;
      this.client.stageChannelMapping(this.onStageChannelMappingSuccess, this.onStageChannelMappingFailure, this._lastStagePayload);
      return;
    }
    this._stageRetried = false;

    const { channelMappings } = this.state;
    const channelMappingsWithStaged = channelMappings.reduce((obj, item) => ({...obj, [item['id']]: item}), {});
    for (const stagedMapping of stagedChannelMappings) {
      const existing = channelMappingsWithStaged[stagedMapping.id];
      channelMappingsWithStaged[stagedMapping.id] = {
        ...stagedMapping,
        _deleted: (existing && existing._deleted) || undefined,
      };
    }
    this.safeSetState({ errorScreenMessage: null,
                    stagedChannelMappings: data.channelMappings,
                    channelMappings: Object.values(channelMappingsWithStaged),
                    channelMappingsById: channelMappingsWithStaged,
    });
  };

  onStageChannelMappingFailure = (error) => {
    logger.error(error);
    this._stageRetried = false;
    this.safeSetState({ errorScreenMessage: extractErrorMessage(error) });
    this.refreshChannelMapping();
  };

  unstageChannelMappingChanges = (modifiedChannelMappingsById, latestChannelMappingsById) => {
    const { channelMappingsById } = this.state;
    let channelMappingsToUnstage = [];
    for (const id of Object.keys(modifiedChannelMappingsById)) {
      let channelMappingIdentifier = {};
      channelMappingsById[id] = { ...latestChannelMappingsById[id] };
      channelMappingIdentifier.id = channelMappingsById[id].id === undefined ? id : channelMappingsById[id].id;
      channelMappingIdentifier.version = this.props.user;
      channelMappingsToUnstage.push(channelMappingIdentifier);
    }

    const payload = this.convertToUnstageRequestPayload(channelMappingsToUnstage);
    this.client.unstageChannelMapping(this.onUnstageChannelMappingSuccess, this.onUnstageChannelMappingFailure, payload);
  };

  convertToUnstageRequestPayload = (channelMappingsToUnstage) => {
    const payload = {};
    payload.channelMappings = channelMappingsToUnstage;
    payload.username = this.props.user;

    return payload;
  };

  onUnstageChannelMappingSuccess = () => {
    this.refreshChannelMapping();
    logger.info('Unstaging channel mapping succeeded.');
    this.safeSetState({ errorScreenMessage: null, successScreenMessage: "Successfully unstaged" });
  };

  onUnstageChannelMappingFailure = (error) => {
    logger.error(error);
    this.safeSetState({ errorScreenMessage: extractErrorMessage(error) });
  };

  publishChannelMappingChanges = (stagedChannelMappingIds, latestChannelMappingsById) => {
    const updatedById = { ...this.state.channelMappingsById };
    let channelMappingsToPublish = [];
    for (const id of Object.keys(stagedChannelMappingIds)) {
      let channelMappingIdentifier = {};
      updatedById[id] = { ...latestChannelMappingsById[id] };
      channelMappingIdentifier.version = this.props.user;
      updatedById[id].last = latestChannelMappingsById[id].last ? latestChannelMappingsById[id].last + 1 : null;
      channelMappingIdentifier.id = updatedById[id].id;
      channelMappingsToPublish.push(channelMappingIdentifier);
    }

    this.safeSetState({ channelMappingsById: updatedById });

    const payload = this.convertToPublishRequestPayload(channelMappingsToPublish);
    this.client.publishChannelMapping(this.onPublishChannelMappingSuccess, this.onPublishChannelMappingFailure, payload);
  };

  convertToPublishRequestPayload = (channelMappingsToPublish) => {
    const payload = {};
    payload.channelMappings = channelMappingsToPublish;
    payload.username = this.props.user;

    return payload;
  };


  onPublishChannelMappingSuccess = (data) => {
    logger.info('Publishing channel mapping succeeded.');
    this.safeSetState({ errorScreenMessage: null, successScreenMessage: "Successfully published" });
    this.refreshChannelMapping();
  };

  onPublishChannelMappingFailure = (error) => {
    logger.error(error);
    this.safeSetState({ errorScreenMessage: extractErrorMessage(error) });
  };

  addChannelMappings = (channelMapping) => {
    let channelMappingsToAdd = Array.isArray(channelMapping) ? channelMapping : [channelMapping];
    const payload = this.convertToAddRequestPayload(channelMappingsToAdd);
    this.client.addChannelMapping(this.onAddChannelMappingSuccess, this.onAddChannelMappingFailure, payload);
  };

  convertToAddRequestPayload = (channelMappingsToAdd) => {
    const payload = {};
    payload.channelMappings = channelMappingsToAdd;
    payload.username = this.props.user;

    return payload;
  };

  onAddChannelMappingFailure = (error) => {
    logger.error(extractErrorMessage(error), error);
    this.safeSetState({ errorScreenMessage: extractErrorMessage(error) });
  };

  onAddChannelMappingSuccess = (result) => {
    this.safeSetState({channelMappings: result.channelMappings, successScreenMessage: "Successfully staged added mapping" });
    this.refreshChannelMapping();
  };

  refreshChannelMapping = (successCallback) => {
    this.client.getChannelMappings((result) => {
      successCallback ? successCallback(result) : this.onChannelMappingsPullSuccess(result)
    }, (error) => this.onChannelMappingsPullFailure(error));
  };

  onChannelMappingsPullFailure = (error) => {
    logger.error(extractErrorMessage(error), error);
    this.safeSetState({ errorScreenMessage: extractErrorMessage(error) });
  };

  onChannelMappingsPullSuccess = (result) => {
    const channelMappingsById = result.data.channelMappings.reduce((obj, item) => ({...obj, [item['id']]: item}), {});
    // Deep copy for immutable original reference (never mutated by staging)
    const originalChannelMappings = result.data.channelMappings.reduce((obj, item) => ({...obj, [item['id']]: {...item}}), {});
    this.refreshDropdownElements(result.data.channelMappings);
    this.safeSetState({ channelMappings: Object.values(channelMappingsById),
                    originalChannelMappings,
    });

    // Getting all staged mappings
    const payload = this.convertToStageRequestPayload([]);
    this._lastStagePayload = null;
    this.client.stageChannelMapping(this.onStageChannelMappingSuccess, this.onStageChannelMappingFailure, payload);
  };

  refreshDropdownElements = (channelMappings) => {
    this.safeSetState({
       dropdownOptions: {
          hcpChannelTypeOptions: getHCPChannelTypeOptions(channelMappings),
          hcpChannelOptions: getHCPChannelOptions(channelMappings),
          cognosChannelOptions: getCognosChannelOptions(channelMappings),
          groupCodeOptions: getGroupCodeOptions(channelMappings),
          marketplaceIdOptions: getMarketplaceIdOptions(this.props.marketplaceConfig),
          cognosParentChannelOptions: getCognosParentChannelOptions(channelMappings),
       }
    })
  };

  onScreenDismiss = () => {
    this.setState({ errorScreenMessage: null, successScreenMessage: null })
  };

  render() {
    const {
      channelMappings,
      errorScreenMessage,
      successScreenMessage,
      channelMappingsById,
      originalChannelMappings
    } = this.state;

    return (
      <div>
      <div>
        <Flashbar
          items={
            errorScreenMessage
            ? [
                {
                  type: 'error',
                  dismissible: true,
                  content: `${ERROR_MESSAGE}${errorScreenMessage}`,
                  onDismiss: () => this.setState({ errorScreenMessage: null }),
                },
              ]
            : []
          }
        />
      </div>
      <div className="awsui-util-container">
        <ChannelMappingTable
          channelMappings={channelMappings}
          channelMappingsById={channelMappingsById}
          onScreenDismiss={this.onScreenDismiss}
          originalChannelMappings={originalChannelMappings}
          addChannelMappings={this.addChannelMappings}
          stageChannelMappingChanges={this.stageChannelMappingChanges}
          unstageChannelMappingChanges={this.unstageChannelMappingChanges}
          publishChannelMappingChanges={this.publishChannelMappingChanges}
          refreshChannelMapping={this.refreshChannelMapping}
          errorScreenMessage={errorScreenMessage}
          successScreenMessage={successScreenMessage}
          dropdownOptions={this.state.dropdownOptions}
          marketplaceConfig={this.props.marketplaceConfig}
          {...this.props}
        />
      </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/config_central/channel_mapping/channel_mapping_container.jsx